import { createAsyncThunk } from "@reduxjs/toolkit";
import { stemProjectApi } from "../services/stemProjectApi";
import { STEM_CATEGORY_ID } from "../config/stemApiConfig";

/**
 * Thunk to fetch active STEM projects by categoryId (default categoryId = 1)
 */
export const fetchStemProjects = createAsyncThunk(
  "stem/fetchStemProjects",
  async (categoryId = STEM_CATEGORY_ID, { rejectWithValue }) => {
    try {
      const data = await stemProjectApi.listActiveByCategory(categoryId);
      return data;
    } catch (err) {
      const message =
        err?.message || "Failed to fetch active STEM projects";
      return rejectWithValue(message);
    }
  }
);

/**
 * Thunk to fetch a single STEM project by id
 */
export const fetchStemProjectById = createAsyncThunk(
  "stem/fetchStemProjectById",
  async (id, { rejectWithValue }) => {
    try {
      const data = await stemProjectApi.getById(id);
      return data;
    } catch (err) {
      const message =
        err?.message || "Failed to fetch project details";
      return rejectWithValue(message);
    }
  }
);

/**
 * Thunk to fetch active stages by projectId using the new API:
 * GET /api/v1/projects/:projectId/stages
 */
export const fetchProjectStages = createAsyncThunk(
  "stem/fetchProjectStages",
  async (projectId, { rejectWithValue }) => {
    try {
      const data = await stemProjectApi.getProjectStages(projectId);
      return data;
    } catch (err) {
      const message =
        err?.message || "Failed to fetch project stages";
      return rejectWithValue(message);
    }
  }
);

/**
 * Thunk to fetch project progress & completed stages
 * GET /lms/stem-progress/projects/:projectId
 */
export const fetchProjectProgress = createAsyncThunk(
  "stem/fetchProjectProgress",
  async (projectId, { rejectWithValue }) => {
    try {
      const { stemProgressApi } = await import("../services/stemProgressApi");
      const summary = await stemProgressApi.getProjectSummary(projectId);
      console.log("[fetchProjectProgress] API response for project", projectId, summary);
      const savedIds = (summary?.progress || [])
        .filter(
          (p) =>
            Number(p.is_complete) === 1 ||
            p.is_complete === true ||
            p.is_complete === "1" ||
            p.isComplete === true ||
            Number(p.isComplete) === 1
        )
        .map((p) => p.stage_id || p.stageId);
      return { projectId, completedStageIds: savedIds, summary };
    } catch (err) {
      const message = err?.message || "Failed to fetch project progress";
      return rejectWithValue(message);
    }
  }
);

/**
 * Thunk to save stage progress to backend
 * POST /lms/stem-progress/stages/:stageId/progress
 */
export const saveStageProgressThunk = createAsyncThunk(
  "stem/saveStageProgress",
  async ({ stageId, projectId, isComplete = true }, { rejectWithValue }) => {
    try {
      const { stemProgressApi } = await import("../services/stemProgressApi");
      await stemProgressApi.saveStageProgress(stageId, projectId, isComplete);
      return { stageId, projectId, isComplete };
    } catch (err) {
      const message = err?.message || "Failed to save stage progress";
      return rejectWithValue(message);
    }
  }
);
